import { Type, Static } from '@sinclair/typebox'
import omit from 'lodash.omit'

import { CountryCodeTB } from './country'
import { ReportSummaryTB, ReportWithInputsTB } from './report'
import { DairyProductionSystemTypeTB } from './report_dairy'
import { PermissionRoleTB } from './permission'
import { MultiPolygonTB, PointTB, PositionTB } from './geojson'

export const FeedlotProductionSystemTypeDrylotTB = Type.Literal('drylot')
export const FeedlotProductionSystemTypeSolidStorageTB = Type.Literal('solid-storage')
export const FeedlotProductionSystemTypeCompostingTB = Type.Literal('composting')
export const FeedlotProductionSystemTypeAnaerobicLagoonTB = Type.Literal('anaerobic-lagoon')

export const FeedlotProductionSystemTypeTB = Type.Union([
  FeedlotProductionSystemTypeDrylotTB,
  FeedlotProductionSystemTypeSolidStorageTB,
  FeedlotProductionSystemTypeCompostingTB,
  FeedlotProductionSystemTypeAnaerobicLagoonTB,
])
export type FeedlotProductionSystemTypeT = Static<typeof FeedlotProductionSystemTypeTB>

export const FeedlotProductionSystemTypeMapTB = Type.Object({
  'Drylot': FeedlotProductionSystemTypeDrylotTB,
  'Solid Storage': FeedlotProductionSystemTypeSolidStorageTB,
  'Composting': FeedlotProductionSystemTypeCompostingTB,
  'Uncovered Anaerobic Lagoon': FeedlotProductionSystemTypeAnaerobicLagoonTB
})
export type FeedlotProductionSystemTypeMapT = Static<typeof FeedlotProductionSystemTypeMapTB>

export const OrganicCarbonHistogramRecordTB = Type.Object({
  interval: Type.Number(),
  value: Type.Number(),
})
export type OrganicCarbonHistogramRecordT = Static<typeof OrganicCarbonHistogramRecordTB>

export const LOOCExistingSoilResponseTB = Type.Object({
  polygonArea: Type.Number(),
  polygonBDAverage: Type.Number(),
  polygonOCPercAverage: Type.Number(),
  surroundingOCPercHistogram: Type.Array(OrganicCarbonHistogramRecordTB),
  warningMessages: Type.Array(Type.String()),
  soilDepth: Type.Number(),
  status: Type.Optional(Type.Number()),
})
export type LOOCExistingSoilResponseT = Static<typeof LOOCExistingSoilResponseTB>

export const CohortToPropertyTB = Type.Object({
  id: Type.String(), // uuid
  cohortId: Type.String(),
  propertyId: Type.String(),
})
export type CohortToPropertyT = Static<typeof CohortToPropertyTB>


export const PropertyTypeFarmTB = Type.Literal('farm')
export type PropertyTypeFarmT = Static<typeof PropertyTypeFarmTB>
export const PropertyTypeFeedlotTB = Type.Literal('feedlot')
export type PropertyTypeFeedlotT = Static<typeof PropertyTypeFeedlotTB>
export const PropertyTypeProcessorTB = Type.Literal('processor')
export type PropertyTypeProcessorT = Static<typeof PropertyTypeProcessorTB>
export const PropertyTypeFurtherProcessorTB = Type.Literal('further-processor')
export type PropertyTypeFurtherProcessorT = Static<typeof PropertyTypeFurtherProcessorTB>
export const PropertyTypeDairyTB = Type.Literal('dairy')
export type PropertyTypeDairyT = Static<typeof PropertyTypeDairyTB>

export const PropertyTypeTB = Type.Union([
  PropertyTypeFarmTB,
  PropertyTypeFeedlotTB,
  PropertyTypeProcessorTB,
  PropertyTypeFurtherProcessorTB,
  PropertyTypeDairyTB,
], { default: undefined })
export type PropertyTypeT = Static<typeof PropertyTypeTB>

export const PropertyTB = Type.Object({
  id: Type.String(), // uuid
  userId: Type.String(), // uuid, TOOD: superseeded by organisationId
  organisationId: Type.String(), // uuid
  propertyType: PropertyTypeTB,
  name: Type.String({ minLength: 1, default: undefined }), // default to undefined for new enterprise form
  geom: Type.Optional(MultiPolygonTB),
  centroid: Type.Optional(PositionTB),
  stateId: Type.Optional(Type.Number()), // N.B. entity def allows for null, but optional rather than nullable in application code
  deletedAt: Type.Optional(Type.Date()), // N.B. entity def allows for null, but optional rather than nullable in application code
  pic: Type.Optional(Type.String({ minLength: 8, maxLength: 8 })), // N.B. entity def allows for null, but optional rather than nullable in application code
  ngr: Type.Optional(Type.String({ minLength: 1 })), // N.B. entity def allows for null, but optional rather than nullable in application code
  rainfall: Type.Optional(Type.Number()), // N.B. entity def allows for null, but optional rather than nullable in application code
  region: Type.Number(),
  area: Type.Number({ default: undefined }),
  cattleFarm: Type.Boolean({ default: false }),
  sheepFarm: Type.Boolean({ default: false }),
  grainFarm: Type.Boolean({ default: false }),
  feedlotProductionSystem: Type.Optional(FeedlotProductionSystemTypeTB),
  beefProcessor: Type.Boolean({ default: false }),
  sheepProcessor: Type.Boolean({ default: false }),
  soilCarbonData: Type.Optional(LOOCExistingSoilResponseTB),
  agriwebbFarmId: Type.Optional(Type.String()), // N.B. entity def allows for null, but optional rather than nullable in application code
  country: CountryCodeTB,
  hasCreatedOneOrMoreReductionPlans: Type.Boolean({ default: false }),
  dairyProductionSystem: Type.Optional(DairyProductionSystemTypeTB)
})
export type PropertyT = Static<typeof PropertyTB>

// N.B. the necessary properties to send to our API for Property creation (depending on the property type)
export const PropertyCreateTB = Type.Composite([
  Type.Required(Type.Pick(PropertyTB, [
    "centroid",
  ])),
  Type.Object({
    organisationId: Type.Optional(Type.String())
  }),
  Type.Pick(PropertyTB, [
    "propertyType",
    "name",
    "area",
    "pic",
    "ngr",
    "agriwebbFarmId",
    "country",
    "cattleFarm",
    "sheepFarm",
    "grainFarm",
    "beefProcessor",
    "sheepProcessor",
    "dairyProductionSystem",
    "feedlotProductionSystem"
  ])
])
export type PropertyCreateT = Static<typeof PropertyCreateTB>

const ValidProductionTypesTB = Type.Union([
  Type.Literal('cattle'),
  Type.Literal('sheep'),
  Type.Literal('grain')
])
export type ValidProductionTypesT = Static<typeof ValidProductionTypesTB>

// N.B. a custom field for the production types, as can't seem to define the minimum number of production types required with cattleFarm, sheepFarm, grainFarm
export const PropertyCreateFormDetailsProductionTypesTB = Type.Object({
  productionTypes: Type.Array(
    ValidProductionTypesTB,
    { minItems: 1, default: undefined }
  )
})
export type PropertyCreateFormDetailsProductionTypesT = Static<typeof PropertyCreateFormDetailsProductionTypesTB>

const ValidProcessorTypesTB = Type.Union([
  Type.Literal('beef'),
  Type.Literal('sheep'),
])
export type ValidProcessorTypesT = Static<typeof ValidProcessorTypesTB>

// N.B. a custom field for the processor types, as can't seem to define the minimum number of processor types required with beefProcessor, sheepProcessor
export const PropertyCreateFormDetailsProcessorTypesTB = Type.Object({
  processorTypes: Type.Array(
    ValidProcessorTypesTB,
    { minItems: 1, default: undefined }
  )
})
export type PropertyCreateFormDetailsProcessorTypesT = Static<typeof PropertyCreateFormDetailsProcessorTypesTB>

export const PropertyCreateFormPropertyTypeTB = Type.Pick(PropertyCreateTB, ["propertyType", "country"])
export type PropertyCreateFormPropertyTypeT = Static<typeof PropertyCreateFormPropertyTypeTB>


export const PropertyCreateFormDetailsBaseFarmTB =Type.Pick(PropertyCreateTB, ["name", "agriwebbFarmId", "area"])
export const PropertyCreateFormDetailsFarmTB = Type.Composite([
  // pic and ngr may be required at runtime depending on the property type, but statically we can't tell
  // hence the need for generatePropertyCreateFormDetailsFarmTB for runtime validation / requirements hinting - see below
  PropertyCreateFormDetailsBaseFarmTB,
  PropertyCreateFormDetailsProductionTypesTB,
  Type.Pick(PropertyCreateTB, ["pic", "ngr"]),
])
export type PropertyCreateFormDetailsFarmT = Static<typeof PropertyCreateFormDetailsFarmTB>

export const PropertyCreateFormDetailsFarmNZTB = Type.Composite([
  // pic and ngr may be required at runtime depending on the property type, but statically we can't tell
  // hence the need for generatePropertyCreateFormDetailsFarmTB for runtime validation / requirements hinting - see below
  Type.Pick(PropertyCreateTB, ["name", "area"]),
  PropertyCreateFormDetailsProductionTypesTB,
])
export type PropertyCreateFormDetailsFarmNZT = Static<typeof PropertyCreateFormDetailsFarmNZTB>

// details form type for a "farm" property is generated at runtime, to allow use of more complex validation / requirements rules re PIC / NGR
export function generateDynamicPropertyCreateFormDetailsFarmTB (formValues?: Partial<PropertyCreateFormDetailsFarmT>) {
  const pic = formValues?.productionTypes?.includes("cattle") || formValues?.productionTypes?.includes("sheep")
    ? Type.Required(Type.Pick(PropertyCreateTB, ["pic"]))
    : Type.Pick(PropertyCreateTB, ["pic"])
  const ngr = formValues?.productionTypes?.includes("grain")
    ? Type.Required(Type.Pick(PropertyCreateTB, ["ngr"]))
    : Type.Pick(PropertyCreateTB, ["ngr"])

  return Type.Composite([
    PropertyCreateFormDetailsBaseFarmTB,
    PropertyCreateFormDetailsProductionTypesTB,
    pic,
    ngr,
  ])
}

// dairy requires a pic and production system
export const PropertyCreateFormDetailsDairyTB = Type.Composite([
  Type.Pick(PropertyCreateTB, ["name", "area"]),
  Type.Required(Type.Pick(PropertyCreateTB, ["pic", "dairyProductionSystem"]))
])
export type PropertyCreateFormDetailsDairyT = Static<typeof PropertyCreateFormDetailsDairyTB>

// feedlot requires a production system
export const PropertyCreateFormDetailsFeedlotTB = Type.Composite([
  Type.Pick(PropertyCreateTB, ["name", "pic", "area"]),
  Type.Required(Type.Pick(PropertyCreateTB, ["feedlotProductionSystem"]))
])
export type PropertyCreateFormDetailsFeedlotT = Static<typeof PropertyCreateFormDetailsFeedlotTB>

export const PropertyCreateFormDetailsProcessorTB = Type.Composite([
  Type.Pick(PropertyCreateTB, ["name", "pic", "propertyType", "area"]),
  PropertyCreateFormDetailsProcessorTypesTB
])
export type PropertyCreateFormDetailsProcessorT = Static<typeof PropertyCreateFormDetailsProcessorTB>

export const PropertyCreateFormDetailsTB = Type.Union([
  PropertyCreateFormDetailsFarmTB,
  PropertyCreateFormDetailsDairyTB,
  PropertyCreateFormDetailsFeedlotTB,
  PropertyCreateFormDetailsProcessorTB
])
export type PropertyCreateFormDetailsT = Static<typeof PropertyCreateFormDetailsTB>

export const PropertyCreateFormLocationTB = Type.Pick(PropertyCreateTB, ["centroid"])
export type PropertyCreateFormLocationT = Static<typeof PropertyCreateFormLocationTB>

export const PropertyCreateFormStandardComponentsTB = Type.Composite([
  PropertyCreateFormPropertyTypeTB,
  PropertyCreateFormLocationTB
])

export const PropertyCreateFormFarmTB = Type.Composite([
  PropertyCreateFormStandardComponentsTB,
  PropertyCreateFormDetailsFarmTB,
])
export type PropertyCreateFormFarmT = Static<typeof PropertyCreateFormFarmTB>

export const PropertyCreateFormDairyTB = Type.Composite([
  PropertyCreateFormStandardComponentsTB,
  PropertyCreateFormDetailsDairyTB,
])
export type PropertyCreateFormDairyT = Static<typeof PropertyCreateFormDairyTB>

export const PropertyCreateFormFeedlotTB = Type.Composite([
  PropertyCreateFormStandardComponentsTB,
  PropertyCreateFormDetailsFeedlotTB,
])
export type PropertyCreateFormFeedlotT = Static<typeof PropertyCreateFormFeedlotTB>

export const PropertyCreateFormProcessorTB = Type.Composite([
  PropertyCreateFormStandardComponentsTB,
  PropertyCreateFormDetailsProcessorTB,
])
export type PropertyCreateFormProcessorT = Static<typeof PropertyCreateFormProcessorTB>

export type PropertyCreateForm = PropertyCreateFormFarmT | PropertyCreateFormDairyT | PropertyCreateFormProcessorT | PropertyCreateFormFeedlotT | PropertyCreateFormDetailsFarmT | PropertyCreateFormDetailsProcessorT

export function transformPropertyCreateUpdateFormToRecord<OutType extends PropertyCreateForm>(data: PropertyCreateForm): OutType {
  const out: OutType = {
    ...data,
    ...omit(data, ['area', 'productionTypes', 'processorTypes']),
    // convert to sq metres from hectares,
    area: data.area * 10000,
    cattleFarm: data && 'productionTypes' in data ? data.productionTypes.includes('cattle') : false,
    sheepFarm: data && 'productionTypes' in data  ? data.productionTypes.includes('sheep') : false,
    grainFarm: data && 'productionTypes' in data ? data.productionTypes.includes('grain') : false,
    beefProcessor: data && 'processorTypes' in data ? data.processorTypes.includes('beef') : false,
    sheepProcessor: data && 'processorTypes' in data ? data.processorTypes.includes('sheep') : false
  } as any as OutType
  return out
}

export function transformPropertyRecordToCreateUpdateForm<OutType extends PropertyCreateForm>(data: PropertyT): OutType {
  const productionTypes: ValidProductionTypesT[] = []
  if (data.cattleFarm) productionTypes.push('cattle')
  if (data.sheepFarm) productionTypes.push('sheep')
  if (data.grainFarm) productionTypes.push('grain')

  const processorTypes: ValidProcessorTypesT[] = []
  if (data.beefProcessor) processorTypes.push('beef')
  if (data.sheepProcessor) processorTypes.push('sheep')

  const out: OutType = {
    ...data,
    ...omit(data, ['area', 'productionTypes', 'processorTypes']),
    // convert to hectares to sq metres,
    area: data.area / 10000,
    productionTypes: productionTypes,
    processorTypes: processorTypes
  } as any as OutType
  return out
}

// createdDate cannot be updated
export const PropertyUpdateTB = Type.Partial(Type.Omit(PropertyTB, ['id', 'createdDate']))
export type PropertyUpdateT = Static<typeof PropertyUpdateTB>

export const PropertyUpdateFormTB = Type.Partial(PropertyCreateFormDetailsTB)
export type PropertyUpdateFormT = Static<typeof PropertyUpdateFormTB>

export const PropertyWithUserRoleTB = Type.Composite([PropertyTB, Type.Object({ role: PermissionRoleTB })])
export type PropertyWithUserRoleT = Static<typeof PropertyWithUserRoleTB>

export const PropertyCreateInviteDTOTB = Type.Object({
  inviteeEmail: Type.String(),
  role: PermissionRoleTB
})
export type PropertyCreateInviteDTOT = Static<typeof PropertyCreateInviteDTOTB>

export const VisionPropertySummaryTB = Type.Object({
  id: Type.String(),
  propertyName: Type.String(),
  propertyType: PropertyTypeTB,
  stateId: Type.Optional(Type.Number()),
  centroid: Type.Optional(PointTB),
  area: Type.Number(),
  country: CountryCodeTB,
  hasCreatedOneOrMoreReductionPlans: Type.Boolean(),
  organisationId: Type.String(),
  sheepFarm: Type.Optional(Type.Boolean()),
  cattleFarm: Type.Optional(Type.Boolean()),
  grainFarm: Type.Optional(Type.Boolean())
})
export type VisionPropertySummaryT = Static<typeof VisionPropertySummaryTB>

export const FarmPropertyFieldsTB = Type.Object({
  propertyType: PropertyTypeFarmTB,
  cattleFarm: Type.Boolean(),
  sheepFarm: Type.Boolean(),
  grainFarm: Type.Boolean()
})
export type FarmPropertyFieldsT = Static<typeof FarmPropertyFieldsTB>

export const FeedlotPropertyFieldsTB = Type.Object({
  propertyType: PropertyTypeFeedlotTB,
  productionSystem: FeedlotProductionSystemTypeTB,
})
export type FeedlotPropertyFieldsT = Static<typeof FeedlotPropertyFieldsTB>

export const ProcessorPropertyFieldsTB = Type.Object({
  propertyType: Type.Union([PropertyTypeProcessorTB, PropertyTypeFurtherProcessorTB]),
  beefProcessor: Type.Boolean(),
  sheepProcessor: Type.Boolean(),
})
export type ProcessorPropertyFieldsT = Static<typeof ProcessorPropertyFieldsTB>

export const DairyPropertyFieldsTB = Type.Object({
  propertyType: PropertyTypeDairyTB,
  dairyProductionSystem: DairyProductionSystemTypeTB
})
export type DairyPropertyFieldsT = Static<typeof DairyPropertyFieldsTB>

export const FarmSummaryTB = Type.Composite([VisionPropertySummaryTB, FarmPropertyFieldsTB])
export type FarmSummaryT = Static<typeof FarmSummaryTB>
export const FeedlotSummaryTB = Type.Composite([VisionPropertySummaryTB, FeedlotPropertyFieldsTB])
export type FeedlotSummaryT = Static<typeof FeedlotSummaryTB>
export const ProcessorSummaryTB = Type.Composite([VisionPropertySummaryTB, ProcessorPropertyFieldsTB])
export type ProcessorSummaryT = Static<typeof ProcessorSummaryTB>
export const DairySummaryTB = Type.Composite([VisionPropertySummaryTB, DairyPropertyFieldsTB])
export type DairySummaryT = Static<typeof DairySummaryTB>

export const PropertySummaryTB = Type.Union([
  FarmSummaryTB,
  FeedlotSummaryTB,
  ProcessorSummaryTB,
  DairySummaryTB
])
export type PropertySummaryT = Static<typeof PropertySummaryTB>

export const PropertyWithOrganisationNameTB = Type.Composite([
  PropertyTB,
  Type.Object({
    organisationName: Type.String()
  })
])
export type PropertyWithOrganisationNameT = Static<typeof PropertyWithOrganisationNameTB>

export const PropertyWithReportSummariesTB = Type.Composite([
  PropertyTB,
  Type.Object({
    reportSummaries: Type.Array(Type.Union([ReportSummaryTB, ReportWithInputsTB]))
  })
])
export type PropertyWithReportSummariesT = Static<typeof PropertyWithReportSummariesTB>
